import type * as PdfJs from 'pdfjs-dist';
import type { Size } from './pdfPageLayout';

/**
 * The only module in this application that knows pdf.js exists (FIL-8b).
 *
 * **It is not here for testability.** It is here because the decisions about
 * how somebody else's evidence file is parsed belong in one place: a PDF is a
 * program-bearing format, and a second call site that opened one differently
 * would be the risk back. The options below are a posture, not configuration.
 *
 * **`isEvalSupported` is deliberately absent, and its absence is the good
 * news.** Earlier pdf.js took that option because its font renderer built
 * glyph procedures with `eval`; version 6 removed the mechanism outright, so
 * there is no longer an option to set and nothing to opt out of. Do not
 * "restore" it from an older guide — it is not in `DocumentInitParameters` and
 * passing it is a type error, which is the one way this could regress quietly.
 * `enableXfa` likewise defaults to false, so XFA forms are never built.
 *
 * That component tests mock this module is a consequence, and the reason is
 * jsdom rather than design: it implements no canvas 2D context, no `Path2D`,
 * no `DOMMatrix`, and no workers, so pdf.js cannot run there at all. That is
 * the same wall `src/test/README.md` documents for `<img>` — jsdom simply has
 * no decoder — and it is why `e2e/pdfViewer.spec.ts` exists to own the one
 * assertion that needs a real one.
 *
 * **The worker is bundled, never fetched from a CDN.** ADR-0048 fixes this
 * application to one origin, and the agencies using it work on networks where
 * an outbound request to a third party is at best slow and at worst blocked.
 * `?url` makes Vite emit the file as an asset and hand back its hashed path.
 *
 * **The image decoders are served from this origin too, and that is not
 * optional (ADR-0106).** Version 6 moved JBIG2 and JPEG 2000 out of the worker
 * into `.wasm` modules pdf.js fetches at runtime from `wasmUrl`. Left unset,
 * `wasmUrl` is nowhere and a scanned black-and-white filing — JBIG2 is the
 * fax-lineage scanner's format, so this is most of the evidence here — fails to
 * decode with `JBig2 failed to initialize` and renders blank. `WASM_URL` names
 * the directory the `pdfjsWasm` Vite plugin serves those files from; the two
 * agree on `pdf-wasm/` by hand, so a drift renders a scanned filing blank.
 *
 * **Standard-14 fonts are substituted from the system, deliberately for now.**
 * `standardFontDataUrl` is unset, so a document using non-embedded Helvetica,
 * Times or Courier is drawn with a local face. Metrics come from pdf.js's own
 * built-in tables, so lines break and text lands where the producer intended;
 * only the glyph shapes differ. Shipping the font data means emitting fourteen
 * files at a stable path, which `publicDir: false` and `publicAssets.test.ts`
 * both make more than a copy step — see ADR-0054. Embedded fonts, which is
 * nearly every real filing, are unaffected.
 */

/**
 * Where pdf.js fetches its `.wasm` image decoders from — a directory URL, which
 * pdf.js requires to end in a slash (it appends `jbig2.wasm` and the rest).
 *
 * Built on `BASE_URL` so it is correct whether the app is served from the root
 * or a sub-path, and resolved by pdf.js against `document.baseURI`. The literal
 * `pdf-wasm` is the one string this module shares with `vite/pdfjsWasm.ts` by
 * hand — see `PDF_WASM_ROUTE` there for why it cannot be imported.
 */
const WASM_URL = `${import.meta.env.BASE_URL}pdf-wasm/`;

/** Why a document would not open, in the terms the reader's copy answers in. */
export type PdfOpenFailure = 'password' | 'invalid' | 'unknown';

export class PdfOpenError extends Error {
  readonly reason: PdfOpenFailure;

  constructor(reason: PdfOpenFailure, cause: unknown) {
    super(`The PDF could not be opened (${reason}).`, { cause });
    this.name = 'PdfOpenError';
    this.reason = reason;
  }
}

export interface PdfRenderOptions {
  canvas: HTMLCanvasElement;
  /** CSS scale. The device ratio is applied on top, by the caller. */
  scale: number;
  /** Total rotation — the page's own plus whatever the reader has turned it. */
  rotation: number;
  /** Canvas pixels per CSS pixel, from `deviceScale`. */
  devicePixelRatio: number;
}

export interface PdfRenderHandle {
  /** Resolves when the page is on the canvas; rejects unless it was cancelled. */
  readonly completed: Promise<void>;
  /**
   * Stop drawing.
   *
   * **Not optional bookkeeping.** Two renders onto one canvas race, and a
   * scroll that outruns the renderer starts one per frame — so a stage that
   * never cancels paints page four's content over page five's and leaves it
   * there, with nothing thrown.
   */
  cancel: () => void;
}

export interface PdfPageHandle {
  /** Size in PDF points, before any rotation is applied. */
  readonly size: Size;
  /** The page's own `/Rotate`, which the reader's rotation composes with. */
  readonly rotation: number;
  render: (options: PdfRenderOptions) => PdfRenderHandle;
  /** Release the page's operator list and images. */
  cleanup: () => void;
}

export interface PdfDocumentHandle {
  readonly pageCount: number;
  getPage: (index: number) => Promise<PdfPageHandle>;
  destroy: () => Promise<void>;
}

/**
 * Loaded once per module, not per document.
 *
 * `await import` rather than a static one so that a folder of photographs never
 * pays for a PDF renderer — it is the largest thing in this application by a
 * wide margin, and Vite splits it into its own chunk on the strength of this
 * line alone.
 */
let library: Promise<typeof PdfJs> | undefined;

async function pdfjs(): Promise<typeof PdfJs> {
  /*
    **The worker's URL is resolved here rather than imported at the top of the
    file**, and it is worth a line. `?url` is a build-time asset reference: Vite
    emits the file and hands back its hashed path either way, so nothing about
    the shipped bundle changes. What changes is that importing this module no
    longer drags a 1.26 MB asset through the transform pipeline — which the
    component tests do on every run, because they stub this module by way of
    `importOriginal`, and which made the PDF viewer's suite the slowest file in
    the workspace by a factor of two.
  */
  library ??= Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib;
  });

  return library;
}

/** pdf.js signals its two expected refusals by `name`, not by an exported class. */
function failureOf(caught: unknown): PdfOpenFailure {
  const name = caught instanceof Error ? caught.name : '';

  if (name === 'PasswordException') return 'password';
  if (name === 'InvalidPDFException') return 'invalid';

  return 'unknown';
}

/**
 * Open a document from bytes this tab already holds.
 *
 * **The bytes are copied, and that is load-bearing.** `getDocument` *transfers*
 * the buffer to the worker, after which the caller's copy is detached and its
 * `byteLength` is 0. `useFileBlob` hands out the one `Blob` behind its cache
 * and signing will want those same bytes again, so a transfer here would empty
 * the cache entry as a side effect of rendering — and the symptom would appear
 * somewhere else entirely, as a zero-byte save.
 */
export async function openPdf(bytes: ArrayBuffer): Promise<PdfDocumentHandle> {
  const lib = await pdfjs();

  const task = lib.getDocument({
    data: bytes.slice(0),
    /*
      The whole file is already in memory, so there is nothing to fetch ahead
      of. Left on, pdf.js schedules background work to pull ranges it already
      has, which on a several-hundred-page filing is measurable and buys
      nothing.
    */
    disableAutoFetch: true,
    /*
      Where the JBIG2 / JPEG 2000 decoders live (ADR-0106). Only this option is
      set, not `cMapUrl` or `standardFontDataUrl`, so pdf.js keeps fetching on
      the main thread against `document.baseURI` rather than switching the worker
      to fetch for itself — which is what makes a relative `WASM_URL` resolve.
    */
    wasmUrl: WASM_URL,
  });

  let document: Awaited<typeof task.promise>;
  try {
    document = await task.promise;
  } catch (caught) {
    void task.destroy();
    throw new PdfOpenError(failureOf(caught), caught);
  }

  return {
    pageCount: document.numPages,

    async getPage(index: number): Promise<PdfPageHandle> {
      // pdf.js counts pages from one; everything above this counts from zero.
      const page = await document.getPage(index + 1);
      const unrotated = page.getViewport({ scale: 1, rotation: 0 });

      return {
        size: { width: unrotated.width, height: unrotated.height },
        rotation: page.rotate,

        render({ canvas, scale, rotation, devicePixelRatio }: PdfRenderOptions): PdfRenderHandle {
          const viewport = page.getViewport({ scale: scale * devicePixelRatio, rotation });

          /*
            **The backing store only. The displayed size belongs to the CSS.**

            A canvas has two sizes and the pair is what makes a page sharp on a
            high-density screen: this many device pixels, shown across that many
            CSS ones. This module used to set both, and that put the display
            size on the slow side of a race. `PdfStage` re-lays the page boxes
            out the moment a zoom commits, but the effect that calls this is a
            *passive* one, which React runs after the browser has painted — so
            for exactly one frame the new, larger box contained a canvas still
            displayed at the old scale. Measured at 1201px out: a stale
            rasterization showing the wrong part of the document at the wrong
            zoom, a fraction of a second after the reader let go.

            The canvas now fills its box in CSS (`PdfStage`, and it is the same
            box `cssWidth`/`cssHeight` give the page), so whatever bitmap it is
            holding is stretched to the right place at the right size. The frame
            in between is soft rather than wrong, and the redraw below sharpens
            it — which is FIL-8b's own preference, applied to the one frame it
            had been losing.
          */
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          const task = page.render({ canvas, viewport });

          return {
            completed: task.promise.catch((caught: unknown) => {
              // A cancel is the reader scrolling on, not a failure to report.
              if (caught instanceof Error && caught.name === 'RenderingCancelledException') return;
              throw caught;
            }),
            cancel: () => {
              task.cancel();
            },
          };
        },

        cleanup() {
          page.cleanup();
        },
      };
    },

    /**
     * `destroy` lives on the loading task, not on the document proxy — calling
     * `document.destroy()` is a `TypeError` at runtime and compiles fine.
     */
    async destroy() {
      await task.destroy();
    },
  };
}
